import { CSSProperties } from 'react';
import { Outlet, useLocation } from 'react-router';
import { AppSidebar } from '@/components/app-sidebar';
import { NavSectionPanel } from '@/components/navigation/nav-section-panel';
import {
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
} from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { DegradedServiceBanner } from '@/components/degraded-service-banner';
import { ScheduledMaintenanceBanner } from '@/components/scheduled-maintenance-banner';
import { SandboxBanner } from '@/components/sandbox-banner';
import { BrandingStyles } from '@/components/branding-styles';
import { IdleTimeoutGuard } from '@/components/idle-timeout-guard';
import { isIdleTimeoutEnabled } from '@/hooks/use-idle-timeout';
import { stripOrgPrefix } from '@/utils/org-path';
import { NAV_RAIL_WIDTH, useNavModeStore } from '@/stores/nav-mode-store';
import { useConnectResultToast } from '@/hooks/use-connect-result-toast';

export const Layout = () => {
  const location = useLocation();
  const navMode = useNavModeStore((state) => state.mode);
  const effectivePath = stripOrgPrefix(location.pathname);

  useConnectResultToast();

  // Check if current route is a workflow route (no header)
  const isWorkflowEditorRoute =
    effectivePath === '/workflow' || effectivePath.startsWith('/workflow/');
  // Artifact viewer gets the same full-bleed treatment
  const isArtifactViewerRoute = /^\/artifacts\/[^/]+/.test(effectivePath);
  const isWorkflowBuilderRoute = effectivePath === '/workflow-builder';
  // Schedules uses a full-width 3-column master-detail layout
  const isSchedulesRoute = effectivePath.startsWith('/schedules');
  // Chat has its own header and docks its prompt bar to the viewport bottom.
  const isChatHomeRoute = effectivePath.startsWith('/chat-home');
  const isFullWidthRoute =
    isWorkflowBuilderRoute || isSchedulesRoute || isChatHomeRoute;
  // These routes have their own in-panel headers, so they drop the app header +
  // breadcrumbs like the workflow editor does.
  const hideHeader =
    isWorkflowEditorRoute ||
    isSchedulesRoute ||
    isArtifactViewerRoute ||
    isWorkflowBuilderRoute ||
    isChatHomeRoute;

  return (
    <div className='relative flex min-h-svh flex-col bg-background'>
      <BrandingStyles />
      {isIdleTimeoutEnabled() && <IdleTimeoutGuard />}
      <SidebarProvider
        data-nav-mode={navMode}
        style={
          { '--sidebar-width-icon': NAV_RAIL_WIDTH[navMode] } as CSSProperties
        }
      >
        <AppSidebar />
        <NavSectionPanel />
        <SidebarInset
          className={
            isFullWidthRoute
              ? 'flex flex-col h-svh'
              : isWorkflowEditorRoute || isArtifactViewerRoute
                ? 'h-svh overflow-hidden'
                : ''
          }
        >
          <DegradedServiceBanner />
          <ScheduledMaintenanceBanner />
          <SandboxBanner />
          {/* Full-bleed routes have no app header to host the mobile trigger,
              which otherwise leaves the nav sheet unreachable on small screens. */}
          {hideHeader && (
            <SidebarTrigger className='fixed left-2 top-2 z-30 bg-background/80 backdrop-blur' />
          )}
          {/* Conditionally render header based on route */}
          {!hideHeader ? (
            <>
              {/* Nothing but the portal renders here on desktop, so collapse
                  the band entirely when no page contributes actions. */}
              <div className='md:has-[#header-actions:empty]:hidden'>
                <header
                  id='app-header'
                  className='flex h-16 shrink-0 items-center gap-2'
                >
                  <div className='flex items-center gap-2 px-4 justify-between w-full'>
                    <SidebarTrigger
                      id='header-sidebar-trigger'
                      className='-ml-1'
                    />
                    {/* Portal target for page-specific header actions */}
                    <div
                      id='header-actions'
                      className='flex items-center gap-2'
                    />
                  </div>
                </header>
                <Separator />
              </div>
              {/* Chat pages need full-width, other pages have padding */}
              {isFullWidthRoute ? (
                <div className='flex-1 min-h-0 overflow-hidden'>
                  <Outlet />
                </div>
              ) : (
                <div className='flex p-8 2xl:max-w-[1280px] w-full self-center h-full'>
                  <Outlet />
                </div>
              )}
            </>
          ) : (
            <Outlet />
          )}
        </SidebarInset>
      </SidebarProvider>
    </div>
  );
};
